"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { UseDraggableOptions, useDraggable } from "@/hooks/use-draggable";
import { cx } from "@/lib/tailwind-utils";
export interface DraggableContainerProps extends UseDraggableOptions {
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Default right position when not dragged (e.g., "50px") */
  defaultRight?: string;
  /** Default bottom position when not dragged (e.g., "50px") */
  defaultBottom?: string;
  /** Default top position when not dragged (e.g., "50px") */
  defaultTop?: string;
  /** Whether to show the component */
  isVisible?: boolean;
  /** z-index value */
  zIndex?: number;
}

/**
 * A draggable floating container that stays within viewport bounds
 *
 * @example
 * ```tsx
 * <DraggableContainer
 *   className="rounded-lg  shadow-lg p-4"
 *   defaultRight="20px"
 *   defaultBottom="20px"
 *   viewportPadding={8}
 * >
 *   <div>Drag me around!</div>
 * </DraggableContainer>
 * ```
 */
export function DraggableContainer(t0) {
  const $ = _c(26);
  let children;
  let className;
  let defaultBottom;
  let defaultRight;
  let defaultTop;
  let draggableOptions;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      className,
      defaultRight,
      defaultBottom,
      defaultTop,
      isVisible: t1,
      zIndex: t2,
      ...draggableOptions
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = defaultBottom;
    $[4] = defaultRight;
    $[5] = defaultTop;
    $[6] = draggableOptions;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    defaultBottom = $[3];
    defaultRight = $[4];
    defaultTop = $[5];
    draggableOptions = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const isVisible = t1 === undefined ? true : t1;
  const zIndex = t2 === undefined ? 40 : t2;
  const {
    containerRef,
    position,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel
  } = useDraggable(draggableOptions);
  if (!isVisible) {
    return null;
  }
  let t3;
  if ($[9] !== className) {
    t3 = cx("fixed cursor-grab", "motion-preset-fade-md motion-scale-in-75", className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = position ? `${position.x}px` : undefined;
  const t5 = position ? `${position.y}px` : defaultTop;
  const t6 = position?.x ? undefined : defaultRight;
  const t7 = position?.y ? undefined : defaultBottom;
  let t8;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== zIndex) {
    t8 = {
      zIndex,
      left: t4,
      top: t5,
      right: t6,
      bottom: t7
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = zIndex;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== children || $[18] !== containerRef || $[19] !== onPointerCancel || $[20] !== onPointerDown || $[21] !== onPointerMove || $[22] !== onPointerUp || $[23] !== t3 || $[24] !== t8) {
    t9 = <div ref={containerRef} className={t3} style={t8} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>{children}</div>;
    $[17] = children;
    $[18] = containerRef;
    $[19] = onPointerCancel;
    $[20] = onPointerDown;
    $[21] = onPointerMove;
    $[22] = onPointerUp;
    $[23] = t3;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
export default DraggableContainer;